import { ISelectType } from "@4uhub/lib4uhub";
import { z } from "zod";

const errorsPath = "page.settings.schedule.scheduleNewCheckIn.errors.";

// Campos "Então" com seleção; no backend cada um vira <Prefixo>ExternalId e <Prefixo>ExternalName
export const THEN_SELECT_FIELDS = [
  "patientEncounterType",
  "complaint",
  "tissAttendanceType",
  "patientEncounterOrigin",
  "patientCareCharacter",
  "serviceClassification",
  "sector",
  "accommodationType",
] as const;

export type TThenSelectField = (typeof THEN_SELECT_FIELDS)[number];

export type TScheduleNewCheckInForm = {
  id?: string;
  tasyScheduleType: ISelectType;
  metersApproximation: string;
  time: string;
  latitude: string;
  longitude: string;

  basicUnitExternalId: string;
  basicUnitExternalName: string;
  complUnitExternalId?: string | null;
  complUnitExternalName?: string | null;

  calendars: ISelectType[];
  insuranceTypes: ISelectType[];
  insurances: ISelectType[];
} & Record<TThenSelectField, ISelectType>;

const requiredSelect = (errorKey: string) =>
  z.object(
    { id: z.string(), name: z.string() },
    {
      required_error: errorsPath + errorKey,
      invalid_type_error: errorsPath + errorKey,
    },
  );

// Aceita vírgula ou ponto decimal (o mapper normaliza para ponto) e exige número dentro da faixa
export const normalizeCoordinate = (value: string) => value.trim().replace(",", ".");

const coordinate = (requiredKey: string, invalidKey: string, limit: number) =>
  z
    .string()
    .min(1, errorsPath + requiredKey)
    .refine((value) => {
      const normalized = normalizeCoordinate(value);
      return /^-?\d+(\.\d+)?$/.test(normalized) && Math.abs(Number(normalized)) <= limit;
    }, errorsPath + invalidKey);

const selectList = z.array(z.object({ id: z.string(), name: z.string() }));

export const scheduleNewCheckInSchema = z.object({
  tasyScheduleType: requiredSelect("tasyScheduleType"),
  metersApproximation: z.string().min(1, errorsPath + "metersApproximation"),
  time: z.string().min(1, errorsPath + "time"),
  latitude: coordinate("latitude", "latitudeInvalid", 90),
  longitude: coordinate("longitude", "longitudeInvalid", 180),

  patientEncounterType: requiredSelect("patientEncounterType"),
  complaint: requiredSelect("complaint"),
  tissAttendanceType: requiredSelect("tissAttendanceType"),
  patientEncounterOrigin: requiredSelect("patientEncounterOrigin"),
  patientCareCharacter: requiredSelect("patientCareCharacter"),
  serviceClassification: requiredSelect("serviceClassification"),
  sector: requiredSelect("sector"),
  accommodationType: requiredSelect("accommodationType"),

  basicUnitExternalId: z.string().min(1, errorsPath + "basicUnitExternalId"),
  basicUnitExternalName: z.string().min(1, errorsPath + "basicUnitExternalName"),

  complUnitExternalId: z.string().nullable().optional(),
  complUnitExternalName: z.string().nullable().optional(),

  calendars: selectList,
  insuranceTypes: selectList,
  insurances: selectList,
});
